import { gql } from '@apollo/client/core';
import ADVENTURES_LIST_FRAGMENT from '../../adventures/fragments';

export const JOB_RECRUIT = gql`
  query JobRecruit($providerId: UUID!) {
    jobRecruit {
      eligibleForRecruit(providerId: $providerId)
      recruitedBySeeker(providerId: $providerId)
    }
  }
`;

export const VIEWER_HAS_MESSAGED = gql`
  query ViewerHasMessaged($accountId: UUID!) {
    account(id: $accountId) {
      viewerHasMessaged
    }
  }
`;

export const VIEWER_CAN_REVIEW = gql`
  query Sitter($accountId: UUID!) {
    account(id: $accountId) {
      ... on Sitter {
        canReview
      }
    }
  }
`;

export const FETCH_BABYSITTER_PROFILE = gql`
  query Babysitter($accountId: UUID!) {
    account(id: $accountId) {
      ... on Sitter {
        id
        distanceFrom
        lastLogin
        displayName
        favoritedSitter
        photoUrl
        photoStatus
        location {
          cityname
          stateabbr
        }
        profiles {
          babysitter {
            profile {
              accessToTransportation
              additionalServices
              ageGroups
              availableAfterSchool
              availableBeforeSchool
              availableDaytime
              availableShortNotice
              availabilityValidUntil
              badges {
                completedAt
                expiresAt
                name
                status
              }
              childCareQualifications
              childWithSpecialNeeds
              comfortableWithPets
              bio
              distanceWillingToTravel
              education {
                currentlyAttending
                levelOfEducation
                major
                schoolName
                yearGraduated
              }
              generalAvailability {
                day
                timeRanges {
                  endTime
                  startTime
                }
              }
              homeworkHelp
              homeworkHelpSubjects
              homeworkHelpType
              idv {
                latestUpdate
                provider
                status
                verdict
              }
              increasePerChildRate
              infantCare
              infantCareExperience
              infantCareServices
              languagesSpoken
              numberOfChildrenAtOneTime
              payRate {
                max
                min
                timeUnit
              }
              qualifications
              sickChildren
              specialNeeds
              specialNeedsCareExperience
              specialNeedsConditionExperience
              specialNeedsRecipientAgeGroups
              specialNeedsServiceExperience
              specialNeedsYearsExperience
              twins
              validDriversLicense
              willingToDriveChildren
              willingToRunErrands
              yearsExperience
            }
            profileVisible
            profileCompleted
            showProfile
          }
          nanny {
            profileVisible
            profileCompleted
            showProfile
            profile {
              bio
            }
          }
          petCare {
            profileVisible
            profileCompleted
            showProfile
            profile {
              bio
            }
          }
          housekeeping {
            profileVisible
            profileCompleted
            showProfile
            profile {
              bio
            }
          }
          companionCare {
            profileVisible
            profileCompleted
            showProfile
            profile {
              bio
            }
          }
        }
        ...AdventuresList
      }
    }
  }
  ${ADVENTURES_LIST_FRAGMENT}
`;

export const FETCH_NANNY_PROFILE = gql`
  query Nanny($accountId: UUID!) {
    account(id: $accountId) {
      ... on Sitter {
        id
        distanceFrom
        lastLogin
        displayName
        favoritedSitter
        photoUrl
        photoStatus
        location {
          cityname
          stateabbr
        }
        profiles {
          babysitter {
            profileVisible
            profileCompleted
            showProfile
            profile {
              bio
            }
          }
          nanny {
            profile {
              accessToTransportation
              additionalServices
              ageGroups
              availabilityBringingChildToJob
              availabilityDuration
              availabilityLiveInProvider
              availabilitySchedule
              availabilityStartDate
              availabilityStartDateFlexible
              availabilityValidUntil
              badges {
                completedAt
                expiresAt
                name
                status
              }
              bio
              childCareQualifications
              childWithSpecialNeeds
              comfortableWithPets
              distanceWillingToTravel
              education {
                currentlyAttending
                levelOfEducation
                major
                schoolName
                yearGraduated
              }
              generalAvailability {
                day
                timeRanges {
                  endTime
                  startTime
                }
              }
              homeworkHelp
              homeworkHelpSubjects
              homeworkHelpType
              idv {
                latestUpdate
                provider
                status
                verdict
              }
              increasePerChildRate
              infantCare
              infantCareExperience
              infantCareServices
              languagesSpoken
              numberOfChildrenAtOneTime
              payRate {
                max
                min
                timeUnit
              }
              payRateTimeUnit
              qualifications
              shortNoticeAvailability
              sickChildren
              specialNeeds
              specialNeedsCareExperience
              specialNeedsConditionExperience
              specialNeedsRecipientAgeGroups
              specialNeedsServiceExperience
              specialNeedsYearsExperience
              twins
              validDriversLicense
              willingToDriveChildren
              willingToRunErrands
              yearsExperience
            }
            profileVisible
            profileCompleted
            showProfile
            profile {
              bio
            }
          }
          petCare {
            profileVisible
            profileCompleted
            showProfile
            profile {
              bio
            }
          }
          housekeeping {
            profileVisible
            profileCompleted
            showProfile
            profile {
              bio
            }
          }
          companionCare {
            profileVisible
            profileCompleted
            showProfile
            profile {
              bio
            }
          }
        }
        ...AdventuresList
      }
    }
  }
  ${ADVENTURES_LIST_FRAGMENT}
`;

export const FETCH_PET_CARE_PROFILE = gql`
  query PetCare($accountId: UUID!) {
    account(id: $accountId) {
      ... on Sitter {
        id
        distanceFrom
        lastLogin
        displayName
        favoritedSitter
        photoUrl
        photoStatus
        location {
          cityname
          stateabbr
        }
        profiles {
          babysitter {
            profileVisible
            profileCompleted
            showProfile
            profile {
              bio
            }
          }
          nanny {
            profileVisible
            profileCompleted
            showProfile
            profile {
              bio
            }
          }
          petCare {
            profile {
              availabilityValidUntil
              badges {
                completedAt
                expiresAt
                name
                status
              }
              bio
              distanceWillingToTravel
              education {
                currentlyAttending
                levelOfEducation
                major
                schoolName
                yearGraduated
              }
              experienceCaringFor
              generalAvailability {
                day
                timeRanges {
                  endTime
                  startTime
                }
              }
              idv {
                error
                latestUpdate
                provider
                status
                verdict
              }
              languagesSpoken
              lastMinuteAvailability
              numberOfPetsAtOneTime
              qualifications
              rates {
                dayRate
                overnightRateOwnerHome
                overnightRateSitterHome
                serviceRate
                sixtyMinuteWalkRate
                thirtyMinuteWalkRate
                trainingRate
                transportationRate
                visitRate
                ratePerService
              }
              services
              yearsExperience
            }
            profileVisible
            profileCompleted
            showProfile
          }
          housekeeping {
            profileVisible
            profileCompleted
            showProfile
            profile {
              bio
            }
          }
          companionCare {
            profileVisible
            profileCompleted
            showProfile
            profile {
              bio
            }
          }
        }
      }
    }
  }
`;

export const FETCH_HOUSEKEEPING_PROFILE = gql`
  query Housekeeping($accountId: UUID!) {
    account(id: $accountId) {
      ... on Sitter {
        id
        distanceFrom
        lastLogin
        displayName
        favoritedSitter
        photoUrl
        photoStatus
        location {
          cityname
          stateabbr
        }
        profiles {
          babysitter {
            profileVisible
            profileCompleted
            showProfile
            profile {
              bio
            }
          }
          nanny {
            profileVisible
            profileCompleted
            showProfile
            profile {
              bio
            }
          }
          petCare {
            profileVisible
            profileCompleted
            showProfile
            profile {
              bio
            }
          }
          housekeeping {
            profileVisible
            profileCompleted
            showProfile
            profile {
              availabilityStartDate
              availabilityStartDateFlexible
              availabilityValidUntil
              badges {
                completedAt
                expiresAt
                name
                status
              }
              bio
              comfortableWithPets
              distanceWillingToTravel
              education {
                currentlyAttending
                levelOfEducation
                major
                schoolName
                yearGraduated
              }
              generalAvailability {
                day
                timeRanges {
                  endTime
                  startTime
                }
              }
              idv {
                error
                latestUpdate
                provider
                status
                verdict
              }
              languagesSpoken
              payRate {
                max
                min
                timeUnit
              }
              servicesExperience
              shortNoticeAvailability
              typesOfServices
              yearsExperience
            }
          }
          companionCare {
            profileVisible
            profileCompleted
            showProfile
            profile {
              bio
            }
          }
        }
      }
    }
  }
`;

export const FETCH_COMPANION_CARE_PROFILE = gql`
  query CompanionCare($accountId: UUID!) {
    account(id: $accountId) {
      ... on Sitter {
        id
        distanceFrom
        lastLogin
        displayName
        favoritedSitter
        photoUrl
        photoStatus
        location {
          cityname
          stateabbr
        }
        profiles {
          babysitter {
            profileVisible
            profileCompleted
            showProfile
            profile {
              bio
            }
          }
          nanny {
            profileVisible
            profileCompleted
            showProfile
            profile {
              bio
            }
          }
          petCare {
            profileVisible
            profileCompleted
            showProfile
            profile {
              bio
            }
          }
          housekeeping {
            profileVisible
            profileCompleted
            showProfile
            profile {
              bio
            }
          }
          companionCare {
            profile {
              ageGroupsExperience
              availabilityDuration
              availabilitySchedule
              availabilityStartDate
              availabilityStartDateFlexible
              availabilityValidUntil
              badges {
                completedAt
                expiresAt
                name
                status
              }
              basicCareServicesExperience
              bio
              comfortableWithPets
              distanceWillingToTravel
              education {
                currentlyAttending
                levelOfEducation
                major
                schoolName
                yearGraduated
              }
              generalAvailability {
                day
                timeRanges {
                  endTime
                  startTime
                }
              }
              healthAndMedicalServices
              services
              idv {
                error
                latestUpdate
                provider
                status
                verdict
              }
              individualizedServices
              languagesSpoken
              personalCareServicesExperience
              qualifications
              rates {
                homeCareRates {
                  hourly
                  fullDay
                  halfDay
                  monthly
                  weekly
                }
                respiteCareRates {
                  fullDay
                  halfDay
                  hourly
                }
                transportationRates {
                  oneWay
                }
              }
              shortNoticeAvailability
              typeOfCareAvailability
              typesOfCareProvided
              willingToProvideTransportation
              willingToRunErrands
              yearsExperience
            }
            profileCompleted
            profileVisible
            showProfile
          }
        }
      }
    }
  }
`;

export const FETCH_REVIEWS = gql`
  query Reviews($accountId: UUID!, $reviewsFetchCount: Int) {
    account(id: $accountId) {
      ... on Sitter {
        reviews(first: $reviewsFetchCount) {
          totalReviewCount
          starAggregate
          ratingsDistribution {
            count
            percent
            stars
          }
          edges {
            node {
              at
              id
              publicFeedback
              response
              reviewerShortName
              stars
            }
          }
        }
      }
    }
  }
`;

export const ADD_FAVORITE_SITTER = gql`
  mutation AddFavoriteSitter($providerId: UUID!) {
    profile {
      addFavoriteSitter(providerId: $providerId) {
        success
      }
    }
  }
`;

export const REMOVE_FAVORITE_SITTER = gql`
  mutation RemoveFavoriteSitter($providerId: UUID!) {
    profile {
      removeFavoriteSitter(providerId: $providerId) {
        success
      }
    }
  }
`;

// TODO: The commented out queries have not been implemented on the backend yet. Add them once they are available
export const profileQueryMap: Record<string, any> = {
  babysitter: FETCH_BABYSITTER_PROFILE,
  nanny: FETCH_NANNY_PROFILE,
  pet_care: FETCH_PET_CARE_PROFILE,
  housekeeping: FETCH_HOUSEKEEPING_PROFILE,
  companion_care: FETCH_COMPANION_CARE_PROFILE,
};
